// IMPORT REACT
import React, {
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import CustomBreadcrumbs from '../../components/BreadcrumbsComponent/CustomBreadcrumbs';
import AddIcon from "@mui/icons-material/Add";
import { EncryptDecryptService } from '../../services/common/EncryptionService';
import {
  Link,
  useLocation,
  useNavigate
} from 'react-router-dom';
import SkeletonWrapper from '../../components/Skeleton/SkeletonWrapper';
import { CustomTextField } from '../../components/CustomFields/TextField';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { PAGE_SIZE } from '../../services/common/const';
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { CustomSelectWithoutForm } from '../../components/CustomFields/CustomSelect';
import { useQuery } from '../../shared/CommonFunction';
import { PreferencesContext } from '../../PreferenceContext';
import { GetDoctorList } from '../../services/DoctorService';
import { AlertType } from '../../services/models/AlertType';
import {
  DeleteReasonForReference,
  GetReasonForReferenceList
} from '../../services/ReasonForReferenceService';
import { GetTeamMemberList } from '../../services/TeamMemberService';

// SCHEMA
interface Data {
  reasonForReferenceId: number;
  code: string;
  authorizePersion: string;
  organization: string;
  designation: string;
  email: string;
  website: string;
  plan: string;
  none: string;
}

type Order = "asc" | "desc" | "";

// SCHEMA FOR TABLE
interface EnhancedTableProps {
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: String;
  rowCount: number;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  reasonForReferenceId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  tabType: number;
  doctorIds: Array<any>;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  reasonForReferenceId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  tabType: 0,
  doctorIds: [],
};


// SCHEMA FOR RESULTDATA
interface ResultData {
  reasonForReferenceColl: Array<any>;
  tabTypeColl: Array<any>;
  totalCount: number;
}

// DEFAULT VALUES FOR RESULT DATA
const initResultData: ResultData = {
  reasonForReferenceColl: [],
  tabTypeColl: [],
  totalCount: 0,
};

// TABLE ROWS FOR SKELETON
const skeletonRowCount = 5;

// Add centralized error handling utility
const handleError = (error: any, context: any, updateContext: any) => {
  updateContext({
    ...context,
    isAlert: true,
    alertMessage: typeof error === "string" ? error : error?.message,
    alertType: AlertType.Error,
  });
};

// Add debounce utility
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const ReasonForReferenceList = () => {
  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [value, setValue] = useState("");
  const [order, setOrder] = React.useState<Order>("");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("none");
  const [rowsPerPage, setRowsPerPage] = React.useState(PAGE_SIZE);
  const [page, setPage] = React.useState(0);
  const [reasonForReferenceId, setReasonForReferenceId] = useState(0);
  const [anchorEl, setAnchorEl3] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [resultData, setResultData] = useState<ResultData>(initResultData);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [doctorData, setDoctorData] = useState<any[]>([]);
  const [counData, setCounData] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageStartIndex, setPageStartIndex] = useState(1);
  const [pageEndIndex, setPageEndIndex] = useState(1);
  const [page_Index, setPage_Index] = useState(1);
  const [Curr_page, setCurrPage] = React.useState(1);
  const [openDeleteDilog, setOpenDeleteDilog] = React.useState(false);
  const [doctorSelected, setDoctorSelected] = useState(false);
  const location = useLocation();

  // GETTTING PARAMS
  let urlParams = useQuery();
  let tab = urlParams.get("tab");
  let startDate: string = "";
  let endDate: string = "";

  let doctorNameFromUrl = urlParams.get("q");
  let doctorIdFromUrl = urlParams.get("doctorId");

  const [param, setParam] = useState({
    ...parameters,
    tabType: tab,
    startDate: startDate,
    endDate: endDate,
    doctorIds:
      doctorIdFromUrl
        ? [
          {
            doctorId: Number(doctorIdFromUrl),
            doctorName: doctorNameFromUrl
              ? decodeURIComponent(doctorNameFromUrl)
              : "",
          },
        ]
        : [],
  });

  // ON SERCH
  function searchValue(val: any) {
    if (val === "") {
      setValue("");
    }
    setPage_Index(1);
    setParam({ ...param, searchValue: val.toString(), pageIndex: 1 });
  }

  // Update the debouncedSearch to use the current date range
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      searchValue(searchTerm);
    }, 500),
    []
  );

  // HANDLE CHECK ROW
  const handleCheckSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = resultData?.reasonForReferenceColl?.map(
        (n: any) => n?.reasonForReferenceId || 0
      );
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  // Update the clear search functionality
  const handleClearSearch = () => {
    setValue(""); // Clear the input value
    searchValue(""); // Clear the search parameter
  };

  // TABLE HEADER
  function EnhancedTableHead(props: EnhancedTableProps) {
    return (
      <TableHead className="table-header-color">
        <TableRow>
          <TableCell
            sx={{
              fontWeight: "bold",
              width: "25%"
            }}
            align="left"
          >
            Doctor Name
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "none",
                sm: "table-cell",
              },
              fontWeight: "bold",
              width: "35%"
            }}
            align="left"
          >
            Location
          </TableCell>
          <TableCell
            sx={{
              display: {
                xs: "none",
                sm: "table-cell",
              },
              fontWeight: "bold",
              width: "40%"
            }}
            align="left"
          >
            Reason For Reference Name
          </TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
    );
  }

  // DESCENDING COMPERISION
  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  // SORTING TABLE
  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number]
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // GET COMPERISION
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // HANDLE POPUP FOR EDIT AND DELETE
  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    reasonForReferenceId: any
  ) => {
    setAnchorEl3(event.currentTarget);
    setReasonForReferenceId(reasonForReferenceId);
  };

  // ON CHANGE PAGE
  const handleChangePageNew = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (Curr_page !== value) {
      setCurrPage(value);
      setPage_Index(value);
      setParam({ ...param, pageIndex: value });
    }
  };

  // CLOSE POPUP
  const handleCloseMore = () => {
    setAnchorEl3(null);
  };

  // HANDLE DELETE WARNING POPUP
  const handleDeleteDilog = (value) => {
    setOpenDeleteDilog(value);
  };

  // HANDLE DELETE
  const deleteRow = async (reasonForReferenceIds: any) => {
    try {
      setAnchorEl3(null);
      handleDeleteDilog(false);
      let DeleteData = {
        reasonForReferenceIds: reasonForReferenceIds?.toString(),
        consumerId: userContext?.consumerId?.toString(),
        organizationId: userContext?.organizationId?.toString(),
        branchId: userContext?.branchId?.toString(),
        isDeleted: true,
        rowManagedBy: userContext?.loginDetail?.userId,
      };
      const result: any = await DeleteReasonForReference(DeleteData);
      if (result) {
        await getReasonForReferenceList();
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result.message,
          alertType: AlertType.Success,
        });
      }
    } catch (e) {
      handleError(e, userContext, updateUserContext);
    }
  }

  // HANDLE UPDATE
  const handleUpdate = (
    reasonForReferenceId: any,
    selectedDoctorOverride?: any
  ) => {
    let encryptreasonForReferenceId: any = edService.encrypt(reasonForReferenceId);
    navigate(
      {
        pathname: "reason-for-reference-save",
        search: `?q=${encodeURIComponent(encryptreasonForReferenceId)}`,
      },
      {
        state: {
          title: " Edit",
          button: "Update",
          reasonForReferenceId: reasonForReferenceId,
          selectedDoctor:
            selectedDoctorOverride ??
            (Array.isArray(param.doctorIds) ? param.doctorIds : []),
        },
      }
    );
  };

  // GET LIST OF DOCTOR
  const fetchDoctorList = async () => {
    try {
      let doctorCollection = [];
      let params = new URLSearchParams();

      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);

      const result: any = await GetTeamMemberList(params);

      if (result?.data?.data?.teamMemberColl) {
        doctorCollection = result?.data?.data?.teamMemberColl;
        setDoctorData(doctorCollection);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  const getReasonForReferenceList = async () => {
    try {
      let params = new URLSearchParams();

      let doctorIds = "";
      if (Array.isArray(param.doctorIds) && param.doctorIds.length > 0) {
        doctorIds = param.doctorIds.map((x) => x.doctorId).join(",");
      }

      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("doctorIds", doctorIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetReasonForReferenceList(params);
      if (result?.data?.data?.reasonForReferenceColl) {
        const resultData: ResultData = result?.data?.data;
        let reasonForReferenceColl = resultData?.reasonForReferenceColl || [];
        let dataCount = resultData?.totalCount || 0;
        let showDataCount = Math.ceil(dataCount / param.pageSize);
        setCounData(showDataCount);
        setResultData(resultData);
        let startIndex = (page_Index - 1) * param.pageSize + 1;
        let endIndex =
          (page_Index - 1) * param.pageSize + (reasonForReferenceColl?.length || 0);
        setPageStartIndex(startIndex);
        setPageEndIndex(endIndex);
        setTotalCount(dataCount);
      } else {
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  const fetchData = async () => {
    try {
      await getReasonForReferenceList();
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USE EFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [param]);

  useEffect(() => {
    fetchDoctorList();
  }, []);

  return (
    <>
      <Box>
        <Box className="main-container">
          <Grid container className="page-header">
            <Grid item xs={6}>
              <Box sx={{ display: "flex", height: "100%", alignItems: "end" }}>
                <CustomBreadcrumbs />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className="page-header-icon">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    const selectedDoctor = Array.isArray(param.doctorIds)
                      ? param.doctorIds[0]
                      : param.doctorIds;
                    // if (!selectedDoctor) {
                    //   setDoctorSelected(true);
                    //   updateUserContext({
                    //     ...userContext,
                    //     isAlert: true,
                    //     alertType: AlertType.Error,
                    //     alertMessage: "Please select the doctor",
                    //   });
                    //   return;
                    // }
                    // setDoctorSelected(false);
                    // handleUpdate(0, selectedDoctor);
                    handleUpdate(0, selectedDoctor);
                  }}
                  size="medium"
                  startIcon={<AddIcon />}>
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid container className="list-container">
            <Grid item xs={12}>
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={12} sm={6} md={3}>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-search-box">
                    <CustomTextField
                      id="searchBox"
                      placeholder="Search..."
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          debouncedSearch(value);
                        }
                      }}
                      type="text"
                      variant="outlined"
                      size="small"
                      className="width-100-percent"
                      onChange={(e) => setValue(e.target.value)}
                      value={value}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              size="small"
                              onClick={() => debouncedSearch(value)}>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        endAdornment: value && (
                          <IconButton
                            aria-label="clear search"
                            onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </SkeletonWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SkeletonWrapper
                    isLoading={isLoading}
                    className="skeleton-search-box">
                    <Box
                      sx={{
                        border: doctorSelected ? '1px solid #d32f2f' : '',
                        borderRadius: '8px',
                      }}
                    >
                      <CustomSelectWithoutForm
                        placeholder="Select Doctor"
                        id={"DoctorSearch"}
                        FieldName={"Doctor"}
                        menuPortalTarget={document.querySelector("body")}

                        handleChange={async (e: any) => {
                          const doctorArray = e
                            ? Array.isArray(e)
                              ? e.map((d) => ({
                                doctorId: d.value,
                                doctorName: d.label
                              }))
                              : [{ doctorId: e.value, doctorName: e.label }]
                            : [];

                          setParam({
                            ...param,
                            doctorIds: doctorArray,
                            pageIndex: 1,
                          });

                          setDoctorSelected(false);
                        }}

                        value={
                          Array.isArray(param.doctorIds)
                            ? param.doctorIds.map((d) => ({
                              label: d.doctorName,
                              value: d.doctorId
                            }))
                            : []
                        }

                        options={doctorData}
                        isClearable
                        isSearchable
                        isNotCreatable
                      />
                    </Box>
                  </SkeletonWrapper>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} mt={2}>
              <TableContainer>
                <Table>
                  <EnhancedTableHead
                    numSelected={resultData?.reasonForReferenceColl?.length || 0}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleCheckSelectAll}
                    rowCount={resultData?.reasonForReferenceColl?.length}
                  />
                  <TableBody>
                    {isLoading ? (
                      Array(skeletonRowCount)
                        ?.fill(0)
                        ?.map((skeletonRow, index) => {
                          return (
                            <TableRow key={"skeleton-row" + index?.toString()}>
                              <TableCell colSpan={8}>
                                <SkeletonWrapper
                                  isLoading={isLoading}
                                  className="skeleton-input">
                                  <span></span>
                                </SkeletonWrapper>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <>
                        {stableSort(
                          resultData?.reasonForReferenceColl,
                          getComparator(order, orderBy)
                        )
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((item: any, index: any) => {
                            return (
                              <TableRow
                                key={`reasonForReference-row-${item?.reasonForReferenceId || index}`}>
                                <TableCell>
                                  <Link
                                    key={`link-${item?.reasonForReferenceId}`}
                                    className="text-link"
                                    to={{
                                      pathname: "reason-for-reference-save",
                                      search: `?q=${encodeURIComponent(
                                        edService.encrypt(
                                          item?.reasonForReferenceId || 0
                                        ) || 0
                                      )}`,
                                    }}>
                                    {item?.doctorName}
                                  </Link>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      sm: "table-cell",
                                    },
                                  }}>
                                  {item?.locationName}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    display: {
                                      xs: "none",
                                      sm: "table-cell",
                                    },
                                  }}>
                                  {
                                    item?.reasonForReferenceName
                                      ? item.reasonForReferenceName.split(", ").length <= 3
                                        ? item.reasonForReferenceName
                                        : `${item.reasonForReferenceName
                                          .split(", ")
                                          .slice(0, 3)
                                          .join(", ")},...`
                                      : ""
                                  }
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="More" placement="top">
                                    <IconButton
                                      key={`more-${item?.reasonForReferenceId}`}
                                      onClick={(e: any) => {
                                        handleOpenMore(e, item?.reasonForReferenceId || 0);
                                      }}>
                                      <MoreVertRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        {resultData?.reasonForReferenceColl?.length <= 0 && (
                          <TableRow>
                            <TableCell align="center" colSpan={8}>
                              No record(s) found
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} paddingTop={1}>
              {resultData?.reasonForReferenceColl?.length > 0 && (
                <Box className="pagination">
                  <Box className="pagination-label">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-label"
                    >
                      {pageStartIndex} - {pageEndIndex} of {totalCount}
                    </SkeletonWrapper>
                  </Box>
                  <Box className="pagination-stack">
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-pagination-stack"
                    >
                      <Stack component="div">
                        <Pagination
                          color="primary"
                          count={counData} // NUMBER OF PAGES
                          page={Curr_page} // CURRENT PAGE
                          onChange={handleChangePageNew} //HANDLE PAGE CHANGE
                        />
                      </Stack>
                    </SkeletonWrapper>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Popover
        elevation={1}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMore}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                handleUpdate(reasonForReferenceId);
              }}>
              <ListItemText primary="Edit" />
            </ListItemButton>
          </ListItem>
          {userContext.loginDetail.userRole !== "user" && (
            <ListItem disablePadding>
              <ListItemButton
                className="delete-container"
                onClick={() => {
                  handleDeleteDilog(true);
                }}>
                Delete
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Popover>
      <Dialog
        open={openDeleteDilog}
        onClose={() => {
          handleDeleteDilog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this reason for reference?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              deleteRow(reasonForReferenceId);
            }}
            className="button-delete">
            Yes
          </Button>
          <Button
            onClick={(e) => {
              handleDeleteDilog(false);
            }}
            autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ReasonForReferenceList
